import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, FormGroup, Input } from 'reactstrap';
import { auth } from '../../Api';
import { formatServerMessage } from '../../utils/serverErrors';
import { deleteSucceeded, deleteFailedMessage, DELETE_UNREACHABLE } from '../../utils/deleteReply';

// Deleting a company (super admin): the server removes the company and everything it owns
// (App\CompanyPurge). Before that, this asks the server what would go (a dry run), lists it, and the admin
// types the company's name to confirm. onDeleted(company) is called once the server has deleted it.
const LABELS = [
	['guards', 'guard', 'guards'],
	['supervisors', 'supervisor', 'supervisors'],
	['clients', 'client', 'clients'],
	['locations', 'client location', 'client locations'],
	['duties', 'duty', 'duties'],
	['district_managers', 'district manager', 'district managers'],
	['districts', 'district', 'districts'],
];

const normalise = (text) => String(text == null ? '' : text).replace(/\s+/g, ' ').trim().toLowerCase();

export default function DeleteCompanyModal({ isOpen, company, onCancel, onDeleted }) {
	const [plan, setPlan] = useState(null);
	const [error, setError] = useState(null);
	const [typed, setTyped] = useState('');
	const [busy, setBusy] = useState(false);
	const companyId = company ? company.id : null;

	useEffect(() => {
		if (!isOpen || companyId == null) {
			return;
		}
		let current = true;
		setPlan(null);
		setError(null);
		setTyped('');
		auth.deleteCompany({ id: companyId, dry_run: true })
			.then((res) => {
				if (!current) return;
				if (res && res.data && res.data.code == 200) {
					setPlan(res.data);
				} else {
					setError(formatServerMessage(res && res.data ? res.data.message : null, 'The company could not be checked.'));
				}
			})
			.catch(() => current && setError(DELETE_UNREACHABLE));
		return () => { current = false; };
	}, [isOpen, companyId]);

	const name = plan && plan.company ? plan.company.name : (company ? company.name : '');
	const blocked = plan && plan.blocked && plan.blocked.length > 0;
	const nameMatches = normalise(typed) !== '' && normalise(typed) === normalise(name);

	const remove = () => {
		setBusy(true);
		setError(null);
		auth.deleteCompany({ id: companyId, confirm_name: typed })
			.then((res) => {
				if (deleteSucceeded(res)) {
					onDeleted(company);
				} else {
					setError(deleteFailedMessage(res, 'The company could not be deleted.'));
				}
			})
			.catch(() => setError(DELETE_UNREACHABLE))
			.then(() => setBusy(false));
	};

	const counts = plan && plan.summary
		? LABELS.filter(([key]) => plan.summary[key] > 0)
			.map(([key, one, many]) => plan.summary[key] + ' ' + (plan.summary[key] == 1 ? one : many))
		: [];

	return (
		<Modal isOpen={isOpen} toggle={busy ? undefined : onCancel}>
			<ModalBody className="alert-body">
				<i className="mdi mdi-alert-circle ft-60"></i>
				<h2>Delete {name}?</h2>
				{!plan && !error ? <div>Checking what belongs to this company…</div> : null}
				{plan ? (
					<div style={{ textAlign: 'left' }}>
						<p>This deletes the company for good, with everything it owns:</p>
						<ul>
							{counts.length ? counts.map((line) => <li key={line}>{line}</li>) : <li>no guards, clients or duties</li>}
							<li>the duties' alerts, rest breaks and tracking</li>
						</ul>
						<p>Everyone signed in for this company, in the admin website and the app, is signed out. This cannot be undone.</p>
						{blocked ? <p style={{ color: '#c00' }}>It cannot be deleted: {plan.blocked.join(' ')}</p> : (
							<FormGroup>
								<label htmlFor="confirm-company-name">Type the company's name to confirm: <b>{name}</b></label>
								<Input id="confirm-company-name" autoComplete="off" value={typed} onChange={(event) => setTyped(event.target.value)} />
							</FormGroup>
						)}
					</div>
				) : null}
				{error ? <p style={{ color: '#c00' }}>{error}</p> : null}
				<FormGroup>
					<Button color="secondary" className="modal-btn ml-1" onClick={onCancel} disabled={busy}>Cancel</Button>
					<span>&nbsp;&nbsp;</span>
					<Button color="danger" className="modal-btn mr-1" onClick={remove} disabled={!plan || blocked || !nameMatches || busy}>
						{busy ? 'Deleting…' : 'Delete for good'}
					</Button>
				</FormGroup>
			</ModalBody>
		</Modal>
	);
}
